import React, { useEffect, useRef, useState } from 'react';
import GenericModal from '~/components/GenericModal/GenericModal';
import { uploadAndParseCsvForRentRoll } from '~/api/resident';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { toast } from 'react-toastify';
import { ReactComponent as FileUploadIcon } from '~/utils/images/file-upload-icon.svg';
import classes from './UploadRentRoll.module.css';
import { AxiosError } from 'axios';

type UploadRentRollCsvProps = {
  onUpload: (data: { rows: any[]; maxColumns: number; file: File }) => void;
  renderMode?: 'modal' | 'inline';
  isVisible?: boolean;
  onClose?: () => void;
  title?: string;
};

const UploadRentRollCsv = (props: UploadRentRollCsvProps) => {
  const renderMode = props.renderMode ?? 'modal';
  const [selectedFile, setSelectedFile] = useState<File>();
  const [isUploading, setIsUploading] = useState(false);
  const { loader } = useFullScreenLoader();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      setSelectedFile(file);
    }
  };

  const uploadFile = async () => {
    const form = new FormData();

    if (!selectedFile) {
      return toast.error('Please select a file to upload first');
    }

    const fileToUpload = selectedFile;

    form.append('file', fileToUpload, 'file');

    setIsUploading(true);
    loader.show();

    try {
      const res = await uploadAndParseCsvForRentRoll(form);
      props.onUpload({ ...res.data, file: fileToUpload });
    } catch (e) {
      const error = e as AxiosError;
      toast.error(error.message);
    } finally {
      setSelectedFile(undefined);
      setIsUploading(false);
      loader.hide();
    }
  };

  useEffect(() => {
    if (renderMode === 'modal' && props.isVisible) {
      setSelectedFile(undefined);
    }
  }, [renderMode, props.isVisible]);

  const dropZone = (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`flex justify-center align-center ${classes.fileUploadWrapper}`}
    >
      <FileUploadIcon />
      <p className='margin-top-32' style={{ fontSize: '18px' }}>
        {selectedFile?.name ? (
          selectedFile.name
        ) : (
          <div>
            Drop your file here or{' '}
            <span
              style={{ textDecoration: 'underline', fontSize: '18px' }}
              onClick={() => inputRef.current?.click()}
            >
              browse
            </span>
            .
          </div>
        )}
      </p>
      <input
        type='file'
        accept='.csv,.xls,.xlsx,.xlsm,.xlsb'
        ref={inputRef}
        className={classes.sheetFileinput}
        onChange={(e) => setSelectedFile(e?.target?.files![0])}
      />
      {renderMode === 'inline' && (
        <button
          onClick={uploadFile}
          disabled={!selectedFile || isUploading}
          className={classes.uploadButton}
          aria-label={isUploading ? 'Uploading file...' : 'Upload file'}
        >
          {isUploading ? (
            <span className={classes.buttonContent}>
              <span className={classes.spinner}></span>
              Uploading...
            </span>
          ) : (
            'Upload'
          )}
        </button>
      )}
    </div>
  );

  if (renderMode === 'inline') {
    return dropZone;
  }

  return (
    <GenericModal
      showModal={!!props.isVisible}
      onChange={() => props.onClose?.()}
      title={props.title ?? ''}
      content={dropZone}
      actionText='Save'
      actionFunction={uploadFile}
    ></GenericModal>
  );
};

export default UploadRentRollCsv;
